import React from 'react';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import { MenuItem, Grid } from '@mui/material';
import { format, parse } from 'date-fns';

interface DateSelectFilterProps {
  onTimePeriodChange?: () => void;
}

const DateSelectFilter: React.FC<DateSelectFilterProps> = ({ onTimePeriodChange }) => {
  const storedTimePeriod = localStorage.getItem('timePeriod') || '';
  const storedStartDate = localStorage.getItem('startDate') || '';
  const storedEndDate = localStorage.getItem('endDate') || '';

  const initialStartDate = storedStartDate
    ? (() => {
        try {
          // Extract only the date part, ignoring any time information
          const datePart = storedStartDate.split('T')[0].split(' ')[0];
          // Parse the date part as MM/dd/yyyy and convert to yyyy-MM-dd
          const parsed = parse(datePart, 'MM/dd/yyyy', new Date());
          return format(parsed, 'yyyy-MM-dd');
        } catch {
          return '';
        }
      })()
    : '';
  const initialEndDate = storedEndDate
    ? (() => {
        try {
          // Extract only the date part, ignoring any time information
          const datePart = storedEndDate.split('T')[0].split(' ')[0];
          // Parse the date part as MM/dd/yyyy and convert to yyyy-MM-dd
          const parsed = parse(datePart, 'MM/dd/yyyy', new Date());
          return format(parsed, 'yyyy-MM-dd');
        } catch {
          return '';
        }
      })()
    : '';

  const [timePeriod, setTimePeriod] = React.useState<string>(storedTimePeriod);
  const [startDate, setStartDate] = React.useState<string>(initialStartDate);
  const [endDate, setEndDate] = React.useState<string>(initialEndDate);

  const handleTimePeriodChange = (event: any) => {
    const newTimePeriod = event.target.value;
    localStorage.setItem('timePeriod', newTimePeriod);
    localStorage.setItem('startDate', '');
    localStorage.setItem('endDate', '');
    setTimePeriod(newTimePeriod);
    setStartDate('');
    setEndDate('');

    // Call the callback function if provided
    if (onTimePeriodChange) {
      onTimePeriodChange();
    }
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newIsoValue = event.target.value;
    try {
      const [yyyy, mm, dd] = newIsoValue.split('-');
      // manually set the time, the backend handles the time conversion to utc
      const formattedDate = `${mm}/${dd}/${yyyy} 00:00:00`;

      // startDate is in local time and backend handles the date conversions
      localStorage.setItem('startDate', formattedDate);
      setStartDate(newIsoValue);

      // Call the callback function if provided
      if (onTimePeriodChange) {
        onTimePeriodChange();
      }
    } catch {
      localStorage.setItem('startDate', '');
      setStartDate('');
    }
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newIsoValue = event.target.value;
    try {
      const [yyyy, mm, dd] = newIsoValue.split('-');
      // manually set the time, the backend handles the time conversion to utc
      const formattedDate = `${mm}/${dd}/${yyyy} 23:59:59`;

      // endDate is in local time and backend handles the date conversions
      localStorage.setItem('endDate', formattedDate);
      setEndDate(newIsoValue);

      // Call the callback function if provided
      if (onTimePeriodChange) {
        onTimePeriodChange();
      }
    } catch {
      localStorage.setItem('endDate', '');
      setEndDate('');
    }
  };

  return (
    <FormControl sx={{ minWidth: 120, marginTop: '7px' }}>
      <>
        <div style={{ margin: '-7px 0 9px' }}>
          <div>
            <span>When</span>
          </div>
        </div>
        <Select
          variant="outlined"
          placeholder="When"
          style={{ marginTop: '-4px', height: '32px', fontSize: '12px' }}
          value={timePeriod}
          onChange={handleTimePeriodChange}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="today">Today</MenuItem>
          <MenuItem value="yesterday">Yesterday</MenuItem>
          <MenuItem value="last 7 days">Last 7 Days</MenuItem>
          <MenuItem value="last 30 days">Last 30 Days</MenuItem>
          <MenuItem value="last 6 months">Last 6 Months</MenuItem>
          <MenuItem value="last 12 months">Last 12 Months</MenuItem>
          <MenuItem value="ytd">Year To Date</MenuItem>
          <MenuItem value="Custom">Custom</MenuItem>
        </Select>
        {timePeriod === 'Custom' && (
          <>
            <div style={{ margin: '-7px 0 24px' }} />
            <Grid container>
              <Grid item>
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiInputBase-input': { padding: '13.5px !important' } }}
                />
              </Grid>
              <Grid item style={{ marginLeft: 8 }}>
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiInputBase-input': { padding: '13.5px !important' } }}
                />
              </Grid>
            </Grid>
          </>
        )}
      </>
    </FormControl>
  );
};

export default DateSelectFilter;
